import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';

import { merge, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ResizedEvent } from './shared/ResizedEvent';

import { IOnHlsLevelsChangedData } from '../hls/IOnHlsLevelsChangedData';
import { VbDualPlaybackLayoutMenuButtonV2Component } from '../hls/dual/VbDualPlaybackLayoutMenuButtonV2.Component';

import { BannerHelper } from './videoBanner/BannerHelper';
import { ChapterHelper } from './videogular/controls/chapters/ChapterHelper';
import { ClosedCaptionsUtilService } from './ClosedCaptionsUtil.Service';
import { DUAL_PLAYBACK_LAYOUT_OPTIONS, DUAL_PLAYBACK_LAYOUT_OPTIONS_V2 } from './DualPlaybackLayoutOptions';
import { DefaultScrubAdapter } from './videogular/controls/scrubAdapter/DefaultScrubAdapter';
import { IAudioTrack } from './IAudioTrack';
import { IBannerDetails } from './videoBanner/BannerTypes';
import { IHeartbeatEventData } from './IHeartbeatEventData';
import { IOnMediaFailoverEvent, IPlaybackChangeEvent, VbPlaybackManagerComponent } from './VbPlaybackManager.Component';
import { IPlayerChapter } from './videogular/controls/chapters/IPlayerChapter';
import { IScrubAdapter } from './videogular/controls/scrubAdapter/IScrubAdapter';
import { ISubtitleOption } from './ISubtitleOption';
import { IThumbnailSheetCfg } from './videogular/controls/IThumbnailSheetCfg';
import { ITimeMarker } from './videogular/controls/VbTimeMarkers.Component';
import { IVbSource } from './IVbSource';
import { IVbrickPlayerConfig, IControlsDisplaySettings } from './IVbrickPlayerConfig';
import { IVideoOverlay } from './videoOverlay/IVideoOverlay';
import { IVideoVerificationConfig } from './IVideoVerificationConfig';
import { MediaErrorCode } from './MediaErrorCode';
import { NativeCrossOrigin } from './CrossOriginType';
import { NativePlaybackUtilService, NativePlayBlacklistTest } from './NativePlaybackUtil.Service';
import { PlayerSizeDetectionService } from './PlayerSizeDetection.Service';
import { PLAYER_USER_PREFERENCE_KEY } from './shared/userPreference/PlayerUserPreferenceHandler';
import { PlayerUserPreferencesService } from 'vbrick-player-src/shared/userPreference/PlayerUserPreference.Service';
import { ReactionCfg } from './reactions/ReactionCfg';
import { VbSpinnerType } from './VbSpinnerType';
import { VbSubtitleMenuButtonComponent } from './VbSubtitleMenuButton.Component';
import { VgFullscreenService } from './videogular/VgFullscreen.Service';
import { VgStates } from './videogular/VgStates';
import { VideoOverlayManager } from './videoOverlay/VideoOverlayManager';
import { VideogularComponent } from './videogular/Videogular.Component';
import { userAgentUtil } from './UserAgentUtil';

import styles from './VbrickPlayer.module.less';

export interface IVbrickPlayerFailoverEvent {
	failoverDetails: IOnMediaFailoverEvent;
}

export interface IVbrickPlayerPlaybackChangeEvent {
	data: IPlaybackChangeEvent;
}

@Component({
	selector: 'vbrick-player',
	host: {
		'[class]': 'styles.root'
	},
	template: `
		<videogular
			#vgApi
			[ngClass]="[
				vgApi.isDualPlaybackActive ? vgApi.dualPlaybackLayout.class : '',
				hasAudioOnly ? styles.audioOnly : '',
				isSmallSizePlayer ? styles.smallSizePlayer : ''
			]"
			[externalAudioTracks]="externalAudioTracks"
			[vgAutoPlay]="!isMobile && playbackOptions.length && autoPlay"
			[vgMutedAutoPlay]="true"
			[forceEndTimeInSec]="forceEndTimeInSec"
			[forceMute]="forceMute"
			[seekForwardMaxTimeInSeconds]="config?.seekForwardMaxTimeInSeconds"
			[vgStartTime]="startTime"
			[disableUserPreferenceStore]="disableUserPreferenceStore"
			(mouseenter)="onMouseEnter()"
			(mouseleave)="onMouseLeave()"
			(vgActiveSourceChange)="onActiveSourceChangeInternal()"
			(vgComplete)="onCompleteInternal()"
			(vgError)="onError($event)"
			(vgPlayerReady)="onPlayerReadyInternal()"
			(vgSeeked)="onSeekedInternal($event.$currentTime)"
			(vgSeeking)="onSeekingInternal($event.$currentTime)"
			(vgUpdateState)="onUpdateStateInternal($event.$state)"
			(vgResetStartTime)="onResetStartTime($event)"
			current-state-class
			[currentState]="vgApi.currentState"
			[isLive]="isLive"
			(vbResized)="onResized($event)"
			[scrubAdapter]="scrubAdapter"
			[chapterHelper]="chapterHelper"
		>

		@if (bannerHelper?.isAnyBannerVisible()) {
			<vb-video-banners
				[bannerHelper]="bannerHelper"
				[isSmallSizePlayer]="isSmallSizePlayer"
				[translations]="translations">
			</vb-video-banners>
		}

		<div [ngClass]="vgApi.chapterHelper?.isImagePresentInAnyChapter() && vgApi.chapterHelper?.showChapterImage ? styles.chapterDisplayed : styles.noChaptersDisplayed">
			<div [ngClass]="vgApi.chapterHelper?.isImagePresentInAnyChapter() && vgApi.chapterHelper?.showChapterImage ? styles.playerContent : styles.noChaptersDisplayed">
				<vg-poster
					[currentState]="vgApi.currentState"
					[hidden]="!vgApi.activeSource || !(posterUrl && vgApi.isSupportedPlaybackOptions && !vgApi.activeSource.isUnavailable)"
					[vgUrl]="posterUrl">
				</vg-poster>

				<vg-media
					[crossOrigin]="nativeCrossOrigin"
					[hidden]="!vgApi.activeSource || vgApi.activeSource.isUnavailable"
					[isControlsDisabled]="disableControls"
					[vgNativePlayBlacklist]="nativePlayBlacklist"
					[vgSrc]="vgApi.sources"
					vb-media-click
					[is360Enabled]="vgApi.activeSource?.is360Enabled"
					[isSmallSizePlayer]="isSmallSizePlayer"
					[controlsSurfaced$]="vgControls.controlSurfaced$"
					[isInitialPlay]="isInitPlayerLoad"
					[overlayControlsVisible]="overlayControlsVisible"
					[videoTitle]="videoTitle">
				</vg-media>

				<vg-conference-dual-hls
					[contentPlayerContainer]="contentPlayerContainer"
					(onHlsLevelsChanged)="onHlsLevelsChanged($event)">
				</vg-conference-dual-hls>

				<vb-dual-playback-content-container
					#contentPlayerContainer
					[hidden]="!vgApi.activeSource || vgApi.activeSource.isUnavailable"
					[isControlsDisabled]="disableControls"
					vb-media-click>
				</vb-dual-playback-content-container>
			</div>

			<vg-chapter-display
				[hidden]="!vgApi.chapterHelper?.isImagePresentInAnyChapter() || !vgApi.chapterHelper?.showChapterImage"
				[ngClass]="styles.chapterDisplay"
				vb-media-click
				[controlsSurfaced$]="vgControls.controlSurfaced$"
				[isControlsDisabled]="disableControls"
				[isInitialPlay]="isInitPlayerLoad"
				[isSmallSizePlayer]="isSmallSizePlayer"
				[overlayControlsVisible]="overlayControlsVisible">
			</vg-chapter-display>
		</div>

		<vb-playback-manager
			#playbackManager
			[currentState]="vgApi.currentState"
			[playbackOptions]="playbackOptions"
			[sources]="vgApi.sources"
			(onCrossOriginSet)="onSetCrossOriginInternal($event)"
			(onCrossOriginError)="onCrossOriginErrorInternal()"
			(onInitialPlayback)="onInitialPlaybackInternal($event)"
			(onManualStreamSwitch)="onManualStreamSwitchInternal($event)"
			(onMediaFailover)="onMediaFailoverInternal($event)"
			(onMulticastError)="onMulticastErrorInternal($event)"
			(onPlaybackError)="onPlaybackErrorInternal($event)">
		</vb-playback-manager>

		<vg-hls
			[closedCaptionsTranslation]="translations.closedCaptions"
			[playbackManager]="playbackManager"
			(onHlsLevelsChanged)="onHlsLevelsChanged($event)">
		</vg-hls>


		<vg-360
			[activeSource]="vgApi.activeSource"
			[hidden]="!vgApi.activeSource || vgApi.activeSource.isUnavailable"
			[isEnabled]="vgApi.activeSource && vgApi.activeSource.is360Enabled"
			[translations]="translations">
		</vg-360>
		@if (!!vgApi.c2paConfig && vgApi.activeSource && vgControls) {
			<vb-c2pa [translations]="translations"
				[activeSource]="vgApi.activeSource"
				[controlSurfaced]="vgControls.controlSurfaced$ | async"
				(failedC2paTimeMarkers)="onFailedC2paTimeMarkers($event)">
			</vb-c2pa>
		}
		@if(reactionCfg) {
			<reactions-player
				[reactions$]="reactionCfg.reactions$"
				[isHidden]="reactionCfg.hidden"
			></reactions-player>
		}

		<button
			[ngClass]="[
				styles.mutedButtonOverlay,
				muteBtnOnLeft || (vgApi.activeSource && vgApi.activeSource.is360Enabled) ? styles.mutedButtonOverlayOnLeft : ''
			]"
			[hidden]="!(vgApi.activeSource &&
				vgApi.currentState === VgStates.PLAY &&
				!vgApi.volume &&
				!vgApi.activeSource.isUnavailable &&
				!forceMute &&
				!disableControls)"
			(click)="onMutedClick()">
			{{ translations.muted }}
		</button>

		@if (!videoVerificationConfig?.isEnabled || videoVerificationConfig?.isVerified) {
			<vb-overlay-play-controls
				[isSmallSizePlayer]="isSmallSizePlayer"
				[isInitialPlay]="isInitPlayerLoad"
				[isInitialPlayNotAtStart]="isInitialPlayNotAtStart"
				[overlayBufferingManager]="bufferingManager"
				[translations]="translations"
				[vbStartAtManager]="vbStartAtManager"
				(overlayControlsDisplayChanged)="overlayControlsVisible = $event">
			</vb-overlay-play-controls>
		}

		@if (videoVerificationConfig?.isEnabled) {
			<vb-overlay-video-verification [config]="videoVerificationConfig"
				[isControlsDisplayed]="vgControlsDisplayed"
				[translations]="translations">
			</vb-overlay-video-verification>
		}

		@if (playerControls.chapterMenu && chapterHelper?.chapters().length && !!vgControls && vgControlsDisplayed) {
			<vg-chapters-menu
				[ngClass]="vgApi.activeSource?.is360Enabled ? styles.chapterMenuWith360 : ''"
				[translations]="translations"
				[scrubAdapter]="scrubAdapter">
			</vg-chapters-menu>
		}

		<vg-controls
			#vgControls
			[currentState]="vgApi.currentState"
			[isInitialPlay]="isInitPlayerLoad"
			[isSmallSizePlayer]="isSmallSizePlayer"
			[forceVgControlsDisplay]="vgApi.forceVgControlsDisplay()"
			[disableControls]="disableControls"
			[hidden]="vgApi.isPlaybackPluginLoading || !vgApi.isSupportedPlaybackOptions || disableControls"
			(showVgControls)="vgControlsDisplayed = $event"
		>

			<ng-container *vgControlDisplay="1">
				<vg-scrub-bar-preview
					*ngIf="isThumbnailEnabled"
					[ngClass]="scrubBarPreviewProperties?.isVisible ? styles.visible : styles.hidden"
					[ngStyle]="{'left': scrubBarPreviewProperties?.position}"
					[timeSecs]="scrubBarPreviewProperties?.timeSecs"
					[chapter]="scrubBarPreviewProperties?.chapter">
				</vg-scrub-bar-preview>
			</ng-container>

			<ng-container *vgControlDisplay="3">
				<vg-scrub-bar
					*ngIf="playerControls.playControls && !isLive"
					[adapter]="scrubAdapter"
					[currentTime]="vgApi.currentTime"
					[isStartAtSeeking]="vbStartAtManager.isStartAtSeeking"
					[isSmallSizePlayer]="isSmallSizePlayer"
					[startAt]="startTime"
					[timeMarkers]="timeMarkers"
					[totalTimeWithFallback]="totalTimeWithFallback"
					[isThumbnailEnabled]="isThumbnailEnabled"
					(scrubBarPreviewEvent)="onScrubBarPreviewEvent($event)">
				</vg-scrub-bar>
			</ng-container>

			<div [ngClass]="styles.controlButtonsBar">
				<span></span>
				<vg-play-pause-button
					*ngIf="playerControls.playControls"
					[hidden]="isLive && vgApi.currentState !== VgStates.STOP">
				</vg-play-pause-button>

				<div
					*ngIf="playerControls.playControls"
					[ngClass]="styles.controlRoot"
					[hidden]="!(isLive && vgApi.currentState !== VgStates.STOP)">
					<button
						[ngClass]="styles.iconButton"
						type="button"
						[attr.aria-label]="translations.stop"
						[tooltip]="translations.stop"
						placement="auto"
						vbControlBarFocus
						(click)="vgApi.stop()">
						<span class="glyphicons stop"></span>
					</button>
				</div>

				<span
					[hidden]="!playerControls.playControls || !isLive"
					[ngClass]="styles.liveIndicator">
					{{ translations.live }}
				</span>

				<vg-time-display
					*vgControlDisplay="4"
					[hidden]="!playerControls.playControls"
					[ngClass]="[
						styles.vgTimeDisplay,
						isLive || !totalTimeWithFallback ? styles.isLiveHide : ''
					]">
					{{ scrubAdapter.getCurrentTime() | vbPlayerTime }} / {{ scrubAdapter.getDuration() | vbPlayerTime }}
				</vg-time-display>

				<div [ngClass]="styles.flex"
					*vgControlDisplay="0">
					<vg-seek
						*ngIf="playerControls.playControls && !isLive"
						[translations]="translations"
						[isBackward]="true">
					</vg-seek>
					<vg-seek
						*ngIf="playerControls.playControls && !isLive"
						[translations]="translations">
					</vg-seek>
				</div>

				<ng-container *vgControlDisplay="2">
					<vg-skip-chapter
						*ngIf="playerControls.chapterSeek && chapterHelper?.chapters().length"
						[translations]="translations">
					</vg-skip-chapter>
				</ng-container>

				<span *vgControlDisplay="0" style="flex-grow: 1"></span>
				<vg-volume
					[translations]="translations"
					[isSmallSizePlayer]="isSmallSizePlayer">
				</vg-volume>

				<ng-container *vgControlDisplay="3">
					<vg-toggle-chapter-display-button
						*ngIf="playerControls.chapterDisplay && chapterHelper?.isImagePresentInAnyChapter()"
						[translations]="translations">
					</vg-toggle-chapter-display-button>
				</ng-container>

				<vb-subtitle-menu-button
					#subtitleMenuComp
					*vgControlDisplay="5"
					[activeSource]="vgApi.activeSource"
					[currentState]="vgApi.currentState"
					[currentTime]="vgApi.currentTime"
					[hidden]="!isSubtitleMenuButtonSurfaced"
					[isCaptionsAvailable]="isCaptionsAvailable"
					[subtitleOptions]="subtitleOptions"
					[translations]="translations"
					[isSmallSizePlayer]="isSmallSizePlayer"
					[forcedCCOn]="config.forcedCCOn"
					[subtitleLangPreferenceOrder]="config.subtitleLangPreferenceOrder">
				</vb-subtitle-menu-button>

				<ng-container *vgControlDisplay="1">
					<vb-dual-playback-layout-menu-button-v2
						*ngIf="!config.showOldDualPlaybackLayout && playerControls.dualLayoutControl && vgApi.isDualPlayback && DUAL_PLAYBACK_LAYOUT_OPTIONS_V2?.length"
						#dualLayoutMenuComp
						[isSmallSizePlayer]="isSmallSizePlayer"
						[layoutOptions]="DUAL_PLAYBACK_LAYOUT_OPTIONS_V2"
						[sources]="vgApi.sources"
						[translations]="translations">
					</vb-dual-playback-layout-menu-button-v2>
				</ng-container>
				@if(reactionCfg?.enabled) {
				<ng-container *vgControlDisplay="0">
					<reactions-menu
						[reactionCfg]="reactionCfg"
						[translations]="translations"
						(onReactionAdded)="onReactionAdded.emit($event)"
						(onReactionsHidden)="toggleReaction($event)"
					>
					</reactions-menu>
				</ng-container>
				}
				<ng-container *vgControlDisplay="6">
					<vb-player-settings-menu
						*ngIf="playerControls.settings"
						[isLive]="isLive"
						[disableHotkeys]="isMobile || config.disableHotkeys"
						[disablePlaybackRate]="playerControls.disablePlaybackRate"
						[translations]="translations"
						[playbackManager]="playbackManager"
						[activeSource]="vgApi.activeSource"
						[playbackOptions]="playbackOptions"
						[toggleSettingsMenu]="toggleSettingsMenu">
					</vb-player-settings-menu>
				</ng-container>

				<vg-fullscreen-button
					*ngIf="!isMobile || !(videoOverlayManager?.activeOverlays$ | async)?.length"
					[hidden]="!playerControls.fullscreen || isFullScreenButtonHidden">
				</vg-fullscreen-button>
				<span></span>
			</div>
		</vg-controls>

		<vb-subtitle-display></vb-subtitle-display>

		<vb-overlay-buffering-manager
			#bufferingManager
			[vbStartAtManager]="vbStartAtManager">
		</vb-overlay-buffering-manager>

		<initial-buffering-manager
			[activeSource]="vgApi.activeSource"
			[currentState]="vgApi.currentState"
			[currentTime]="vgApi.currentTime"
			[overlayBufferingManager]="bufferingManager"
			(onInitialBufferingChange)="onInitialBufferingChange($event.isInitialBuffering)">
		</initial-buffering-manager>

		<vb-overlay-buffering
			[isBuffering]="bufferingManager.isBuffering"
			[experiencedBufferingThresholdInMs]="experiencedBufferingThresholdInMs"
			[isSmallSizePlayer]="isSmallSizePlayer"
			(onBufferingSpinner)="bufferingSpinner($event.duration)">
		</vb-overlay-buffering>


		<vb-playback-plugin-loading
			[ngClass]="styles.vbPlaybackPluginLoading"
			[hidden]="!vgApi.isPlaybackPluginLoading">
			{{ translations.loadingPlugin }}
		</vb-playback-plugin-loading>

		<vb-no-supported-playbacks
			[hidden]="vgApi.isSupportedPlaybackOptions !== false"
			[isSmallSizePlayer]="isSmallSizePlayer"
			[playbackManager]="playbackManager"
			[translations]="translations">
		</vb-no-supported-playbacks>

		<vb-playback-unavailable
			[hidden]="!vgApi.activeSource || !vgApi.activeSource.isUnavailable"
			[isSmallSizePlayer]="isSmallSizePlayer"
			[mediaError]="mediaError"
			[translations]="translations">
		</vb-playback-unavailable>

		<hls-level-picker
			*ngIf="debugMode && hlsLevelsModel && vgApi.activeSource && !vgApi.activeSource.isUnavailable"
			[hlsLevelsModel]="hlsLevelsModel">
		</hls-level-picker>

		<vb-start-at-manager
			#vbStartAtManager
			[currentTime]="vgApi.currentTime"
			[playbackOptions]="playbackOptions"
			[startAt]="startTime"
			[startAtResume]="!!startAtResume"
			[totalTime]="vgApi.totalTime">
		</vb-start-at-manager>

		<vb-playerjs-receiver
			[currentState]="vgApi.currentState"
			[currentTime]="vgApi.currentTime"
			[isComplete]="isComplete">
		</vb-playerjs-receiver>

		<ng-container
			*ngIf="!isMobile && !config.disableHotkeys"
			vbHotkeysRegistration
			[translations]="translations"
			[ccButtonVisible]="isSubtitleMenuButtonSurfaced"
			[isVolumeVisible]="!forceMute"
			[isDualPlayback]="playerControls.dualLayoutControl && vgApi.isDualPlayback">
		</ng-container>

	</videogular>`,
    standalone: false
})
export class VBrickPlayerComponent implements AfterViewInit, OnDestroy, OnChanges, OnInit {
	@Input() public autoLoad: boolean;
	@Input() public autoPlay: boolean;
	@Input() public autoShowChapterImages: boolean;
	@Input() public bannerDetails: IBannerDetails;
	@Input() public chapters: IPlayerChapter[];
	@Input() public config: IVbrickPlayerConfig;
	@Input() public debugMode: boolean;
	@Input() public disableControls: boolean;
	@Input() public durationFallback: number; // seconds. Used before the totalTIme from the playback is known.
	@Input() public experiencedBufferingThresholdInMs: number;
	@Input() public externalAudioTracks: IAudioTrack[];
	@Input() public flashDisabled: boolean;
	@Input() public forceEndTimeInSec: number;
	@Input() public forceMute: boolean;
	@Input() public hasAudioOnly: boolean;
	@Input() public heartbeatInterval: number; // seconds
	@Input() public isCaptionsAvailable: boolean;
	@Input() public isLive: boolean;
	@Input() public muteBtnOnLeft: boolean;
	@Input() public playbackOptions: IVbSource[];
	@Input() public posterUrl: string;
	@Input() public scrubAdapter: IScrubAdapter;
	@Input() public startAtResume: boolean;
	@Input() public startTime: number; // seconds
	@Input() public subtitleOptions: ISubtitleOption[];
	@Input() public thumbnailCfg: IThumbnailSheetCfg;
	@Input() public timeMarkers: ITimeMarker[];
	@Input() public translations: any;
	@Input() public videoTitle: string;
	@Input() public videoVerificationConfig: IVideoVerificationConfig;
	@Input() public videoOverlays: IVideoOverlay[];
	@Input() public reactionCfg: ReactionCfg;
	@Input() public disableUserPreferenceStore: boolean;

	@Output() public onBufferingSpinner: EventEmitter<{ duration: number; type: string }> = new EventEmitter();
	@Output() public onBufferingStart: EventEmitter<{ currentTime: number; isInitial: boolean }> = new EventEmitter();
	@Output() public onBufferingStop: EventEmitter<{ duration: number; isInitial: boolean }> = new EventEmitter();
	@Output() public onComplete: EventEmitter<{ duration: number; src: string }> = new EventEmitter();
	@Output() public onCrossOriginError: EventEmitter<void> = new EventEmitter();
	@Output() public onFailover: EventEmitter<IVbrickPlayerFailoverEvent> = new EventEmitter();
	@Output() public onHeartbeat: EventEmitter<{ data: IHeartbeatEventData }> = new EventEmitter();
	@Output() public onInitialPlayback: EventEmitter<IVbrickPlayerPlaybackChangeEvent> = new EventEmitter();
	@Output() public onManualStreamSwitch: EventEmitter<IVbrickPlayerPlaybackChangeEvent> = new EventEmitter();
	@Output() public onMulticastError: EventEmitter<{ data: any }> = new EventEmitter();
	@Output() public onPause: EventEmitter<{ currentTime: number; src: string }> = new EventEmitter();
	@Output() public onPlay: EventEmitter<{ currentTime: number; isInitial: boolean; src: string }> = new EventEmitter();
	@Output() public onPlaybackError: EventEmitter<{ data: any }> = new EventEmitter();
	@Output() public onPlaybackStarted: EventEmitter<IVbrickPlayerPlaybackChangeEvent> = new EventEmitter();
	@Output() public onPlayerReady: EventEmitter< { vgAPI: VideogularComponent, player: VBrickPlayerComponent }> = new EventEmitter();
	@Output() public onSeek: EventEmitter<{ startTime: number; endTime: number }> = new EventEmitter();
	@Output() public onStop: EventEmitter<{ src: string }> = new EventEmitter();
	@Output() public onPlayerSizeChanged: EventEmitter<{ height: number, width: number }> = new EventEmitter();
	@Output() public onOverlayAdded: EventEmitter<IVideoOverlay> = new EventEmitter();
	@Output() public onOverlayRemoved: EventEmitter<IVideoOverlay> = new EventEmitter();
	@Output() public onReactionAdded: EventEmitter<string> = new EventEmitter();

	@ViewChild('playbackManager', { static: true }) private playbackManager: VbPlaybackManagerComponent;
	@ViewChild('vgApi', { static: true }) private vgApi: VideogularComponent;
	@ViewChild('subtitleMenuComp') public subtitleMenuComp: VbSubtitleMenuButtonComponent;
	@ViewChild('dualLayoutMenuComp') public dualLayoutMenuComp: VbDualPlaybackLayoutMenuButtonV2Component;

	public hlsLevelsModel: IOnHlsLevelsChangedData;
	public isComplete: boolean;
	public isFullScreenButtonHidden: boolean;
	public isInitPlayerLoad: boolean;
	public isInitialPlay: boolean;
	public isInitialPlayNotAtStart: boolean;
	public isMobile: boolean;
	public isSeeking: boolean;
	public isSmallSizePlayer: boolean;
	public isThumbnailEnabled: boolean;
	public mediaError: any;
	public nativeCrossOrigin: string;
	public nativePlayBlacklist: NativePlayBlacklistTest[];
	public overlayControlsVisible: boolean;
	public playerControls: IControlsDisplaySettings;
	public scrubBarPreviewProperties: any;
	public toggleSettingsMenu: boolean;
	public videoOverlayManager: VideoOverlayManager;
	public chapterHelper: ChapterHelper;
	public vgControlsDisplayed: boolean;
	public videoVerificationOverlayDisplayed: boolean;
	public bannerHelper: BannerHelper;

	public readonly DUAL_PLAYBACK_LAYOUT_OPTIONS = DUAL_PLAYBACK_LAYOUT_OPTIONS;
	public readonly DUAL_PLAYBACK_LAYOUT_OPTIONS_V2 = DUAL_PLAYBACK_LAYOUT_OPTIONS_V2;
	public readonly VgStates = VgStates;
	public readonly styles = styles;

	private bufferingStartTime: Date;
	private errorRaised: boolean;
	private heartbeatIntervalRef: number;
	private isBuffering: boolean;
	private isClosedCaptionsSupported: boolean;
	private isDestroyed: boolean;
	private isInitialBuffering: boolean;
	private isRewind: boolean;
	private isStateUpdatedPriorToVgApi: boolean;
	private playOnActiveSourceChange: boolean;
	private playerState: VgStates;
	private seekStartTime: number;
	private subscriptions: Subscription[] = [];

	constructor(
		private el: ElementRef<HTMLElement>,
		private ClosedCaptionsUtilService: ClosedCaptionsUtilService,
		private NativePlaybackUtil: NativePlaybackUtilService,
		private PlayerSizeDetectionService: PlayerSizeDetectionService,
		private readonly playerUserPreferenceService: PlayerUserPreferencesService,
		private vgFullscreen: VgFullscreenService
	) {}

	public ngOnInit(): void {
		this.assignPlayerConfig();
		this.errorRaised = false;
		this.isClosedCaptionsSupported = false;
		this.isComplete = false;
		this.isFullScreenButtonHidden = !this.vgFullscreen.isAvailable && window.self !== window.top, // no Fullscreen API AND framed
		this.isInitialPlay = true;
		this.isInitialPlayNotAtStart = this.isInitialPlay && this.startTime !== 0 && !isNaN(this.startTime);
		this.isInitPlayerLoad = true;
		this.isMobile = userAgentUtil.isMobile;
		this.isRewind = false;
		this.isSeeking = false;
		this.isStateUpdatedPriorToVgApi = false;
		this.nativePlayBlacklist = this.NativePlaybackUtil.getNativePlayBlacklist();
		this.isThumbnailEnabled = !!this.thumbnailCfg?.pageUrls.length;
		this.vgApi.flashDisabled = this.flashDisabled;
		this.bannerHelper = new BannerHelper(this.bannerDetails || { isEnabled: false, banners: [] });


		this.subscriptions.push(
			this.initializeOnPlaybackStarted()
		);

		this.scrubAdapter = this.scrubAdapter || new DefaultScrubAdapter(
			this.vgApi.currentTimeSignal,
			() => this.totalTimeWithFallback
		);

		this.chapterHelper = new ChapterHelper(this.chapters || [], this.scrubAdapter.getCurrentTime as any);
		this.chapterHelper.setShowChapterImage(this.autoShowChapterImages);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.startTime) {
			this.onUpdateStartTime();
		}

		if (changes.heartbeatInterval) {
			this.onHeartbeatIntervalChange();
		}

		if (changes.config) {
			this.assignPlayerConfig();
		}

		if (changes.videoOverlays && !changes.videoOverlays.isFirstChange()) {
			this.manageVideoOverlays();
		}

		if (changes.chapters) {
			this.chapterHelper?.setChapters(this.chapters);
		}

		if (changes.bannerDetails) {
			this.bannerHelper?.updateBannerDetails(this.bannerDetails);
		}
	}

	public ngAfterViewInit(): void {
		this.vgApi.autoLoad = this.autoLoad;
		this.manageVideoOverlays();
	}

	public ngOnDestroy(): void {
		this.isDestroyed = true;

		this.videoOverlayManager?.cleanupOverlays();

		//clean up the heartbeat interval
		this.stopHeartbeat();

		//Player is raising error event even if scope is destroyed.
		this.errorRaised = true;

		this.subscriptions.forEach(sub => sub.unsubscribe());
	}

	public get isSubtitleMenuButtonSurfaced(): boolean {
		const isSubtitles = this.subtitleOptions && this.subtitleOptions.length && !this.isLive;
		const isCaptions = this.isCaptionsAvailable && this.isClosedCaptionsSupported;

		return this.playerControls.cc && (
			isSubtitles || isCaptions || this.vgApi?.rawMediaElement?.textTracks?.length > 0 || !!this.vgApi?.audioTracks?.length
		);
	}

	public get totalTimeWithFallback(): number {
		return this.vgApi ?
			this.vgApi.totalTime || (this.durationFallback * 1000)
			: null;
	}

	private assignPlayerConfig(): void {
		this.playerControls = {
			overlayControls: true,
			overlaySeekControls: true,
			playControls: true,
			cc: true,
			dualLayoutControl: true,
			settings: true,
			fullscreen: true,
			chapterSeek: true,
			chapterDisplay: true,
			chapterMenu: true,
			...this.config.playerControls
		};
		this.vgApi.c2paConfig = this.config.c2paConfig;
	}

	private isBogusSeekEvent(start: number, end: number): boolean {
		const seekDifference: number = end - start;

		return seekDifference >= 0 && seekDifference < 0.5; //may be the same value or a tiny nudge
	}

	public onScrubBarPreviewEvent(event: { position, isVisible, mouseMoveEvent }): void {
		this.scrubBarPreviewProperties = event;
	}

	public onActiveSourceChangeInternal(): void {
		this.isInitialPlay = true;
		this.isInitialPlayNotAtStart = this.isInitialPlay && this.startTime !== 0 && !isNaN(this.startTime);
		this.isComplete = false;
		this.isSeeking = false;
		this.isRewind = false;

		this.isClosedCaptionsSupported = this.vgApi && this.ClosedCaptionsUtilService.isClosedCaptionsSupported(this.vgApi.activeSource);

		if (this.playOnActiveSourceChange && this.vgApi.activeSource) {
			this.playOnActiveSourceChange = false;

			this.onPlayInternal();
		}
	}

	public onSetCrossOriginInternal(value: NativeCrossOrigin): void {
		this.nativeCrossOrigin = value;
	}

	private onBufferingStartInternal(): void {
		this.bufferingStartTime = new Date();

		if (this.onBufferingStart) {
			this.onBufferingStart.emit({ currentTime: this.vgApi.currentTime, isInitial: this.isInitialBuffering });
		}

		this.stopHeartbeat();
	}

	private onBufferingStopInternal(): void {
		const activeSource = this.vgApi.activeSource;

		if (!activeSource || activeSource.isUnavailable) {
			return;
		}

		const duration = new Date().getTime() - this.bufferingStartTime.getTime();

		if (this.onBufferingStop && !isNaN(duration)) {
			this.onBufferingStop.emit({ duration, isInitial: this.isInitialBuffering });
		}

		this.bufferingStartTime = null;

		this.startHeartbeat();
	}

	public onCompleteInternal(): void {
		this.stopHeartbeat();

		//rewind
		if (!this.vgApi.activeSource.isLive) {
			this.vgApi.analyticsEnabled = false;
			this.isRewind = true;
			this.vgApi.seekTime(0);

			//workaround playback continuing in IE
			this.vgApi.pause();

			this.vgApi.analyticsEnabled = true;
		}

		if (this.vgApi.analyticsEnabled && !this.isComplete) {
			this.onComplete.emit({ src: this.vgApi.activeSource.src, duration: this.vgApi.totalTime });
		}

		this.isComplete = true;
	}

	public bufferingSpinner(duration: number): void {
		if(!duration) {
			return;
		}

		this.onBufferingSpinner.emit({ type: this.getSpinnerType(), duration });
	}

	private getSpinnerType(): string {
		// for now only handling buffering and initial;
		return this.isInitialBuffering ? VbSpinnerType.INITIAL : VbSpinnerType.BUFFERING;
	}

	private initializeOnPlaybackStarted(): Subscription {
		const playbackUpdated$ = merge(
			this.onInitialPlayback,
			this.onManualStreamSwitch,
			this.onFailover
		).pipe(
			map(event => {
				const playbackChangeEvent = event as IVbrickPlayerPlaybackChangeEvent;

				if (playbackChangeEvent.data) {
					return playbackChangeEvent;
				}

				const { failoverDetails } = event as IVbrickPlayerFailoverEvent;

				return {
					data: {
						isFlash: failoverDetails.isToFlash,
						playback: failoverDetails.playback,
						src: failoverDetails.toUrl
					}
				} as IVbrickPlayerPlaybackChangeEvent;
			})
		);

		return playbackUpdated$.pipe(
			switchMap(event =>
				this.vgApi.playbackStarted$.pipe(
					map<any, IVbrickPlayerPlaybackChangeEvent>(() => event)
				)
			)
		).subscribe(event => this.onPlaybackStarted.emit(event));
	}

	public onCrossOriginErrorInternal(): void {
		if (!this.errorRaised) {
			this.errorRaised = true;

			if (this.onCrossOriginError) {
				this.onCrossOriginError.emit();
			}
		}
	}

	public onError($event: any): void {
		if (this.isDestroyed) {
			return;
		}

		this.mediaError = $event;

		const mediaElement = this.vgApi.mediaElement;
		const nativeMediaError = mediaElement.error ?
			mediaElement.error.code + (mediaElement.error.message || '') :
			undefined;

		console.error('vbrick-player media error', nativeMediaError);

		// ignore decoding errors when the API flag is set to allow for alternative handling / recovery
		if (this.vgApi.isIgnoreDecodingErrors && nativeMediaError && mediaElement.error.code === MediaErrorCode.DECODE) {
			console.info('vbrick-player configured to ignore decoding errors');
			return;
		}

		//no other error handling hook supplied, so broadcast this to other interested components
		this.playbackManager.mediaError($event);
	}

	private onHeartbeatInternal(): void {
		if (this.onHeartbeat) {
			this.onHeartbeat.emit({
				data: {
					bandwidth: this.vgApi.bandwidth,
					bitrate: this.vgApi.bitrate,
					timeInVideo: this.vgApi.currentTime,
					volume: this.vgApi.volume,
					when: new Date()
				}
			});
		}
	}

	private onHeartbeatIntervalChange(): void {
		//restart the heartbeat so that it runs on the new interval
		if (this.heartbeatIntervalRef) {
			this.stopHeartbeat();
			this.startHeartbeat();
		}
	}

	public onHlsLevelsChanged(event: IOnHlsLevelsChangedData): void {
		this.hlsLevelsModel = event;
	}

	public onInitialBufferingChange(isInitialBuffering: boolean): void {
		this.isInitialBuffering = isInitialBuffering;
	}

	public onInitialPlaybackInternal($event: IPlaybackChangeEvent): void {
		this.errorRaised = false;

		if (this.onInitialPlayback) {
			this.onInitialPlayback.emit({ data: $event });
		}
	}

	public onManualStreamSwitchInternal($event: IPlaybackChangeEvent): void {
		this.errorRaised = false;

		if (this.onManualStreamSwitch) {
			this.onManualStreamSwitch.emit({ data: $event });
		}
	}

	public onMediaFailoverInternal($event: IOnMediaFailoverEvent): void {
		this.stopHeartbeat();

		this.errorRaised = false;

		if (this.onFailover) {
			this.onFailover.emit({ failoverDetails: $event });
		}
	}

	public onMouseEnter(): void {
		const classList = this.el.nativeElement.classList;

		classList.remove('mouse-leave');
		classList.add('mouse-enter');
	}

	public onMouseLeave(): void {
		const classList = this.el.nativeElement.classList;

		classList.remove('mouse-enter');
		classList.add('mouse-leave');
	}

	public onMulticastErrorInternal($event: any): void {
		this.stopHeartbeat();

		if (!this.errorRaised) {
			this.errorRaised = true;

			if (this.onMulticastError) {
				this.onMulticastError.emit({ data: $event });
			}
		}
	}

	public onMutedClick(): void {
		this.vgApi.setVolume(1);
		this.playerUserPreferenceService.setPreference<number>(PLAYER_USER_PREFERENCE_KEY.VOLUME, 1);
	}

	private onPauseInternal(): void {
		this.playerState = VgStates.PAUSE;

		if (!this.isComplete && !this.isLive && this.vgApi.currentTime !== this.vgApi.totalTime && this.vgApi.activeSource) { //ignore pause state at complete
			this.onPause.emit({ src: this.vgApi.activeSource.src, currentTime: this.vgApi.currentTime });

			this.stopHeartbeat();
		}
	}

	private onPlayInternal(): void {
		this.errorRaised = false;
		this.playerState = VgStates.PLAY;

		if (this.vgApi.activeSource) {
			this.onPlay.emit({ src: this.vgApi.activeSource.src, isInitial: this.isInitialPlay, currentTime: this.vgApi.currentTime });

			this.isInitialPlay = false;
			this.isInitialPlayNotAtStart = false;
			this.isComplete = false;
			this.isInitPlayerLoad = false;

			this.startHeartbeat(VgStates.PLAY);
		} else { //activeSource not populated yet, so watch for it
			this.playOnActiveSourceChange = true;
		}
	}

	public onPlaybackErrorInternal($event: any): void {
		this.stopHeartbeat();

		if (!this.errorRaised) {
			this.errorRaised = true;

			if (this.onPlaybackError) {
				this.onPlaybackError.emit({ data: $event });
			}
		}
	}

	public onPlayerReadyInternal(): void {
		//set our flag to enable analytics
		this.vgApi.analyticsEnabled = true;

		//apply custom buffering flag setter so that we may better track changes
		this.isBuffering = this.vgApi.isBuffering;

		this.vgApi.config = this.config || {};

		Object.defineProperties(this.vgApi, {
			isBuffering: {
				get: () => {
					return this.isBuffering || this.vgApi.isBufferingOverride || this.isSeeking;
				},

				set: (value: boolean) => {
					const isChange: boolean = this.isBuffering !== value;
					const isRemainsFalsy: boolean = typeof this.isBuffering === 'undefined' && !value;

					//short-circuit if it's a bogus buffering start received while a live stream is stopped
					if (value && this.isLive && this.vgApi.currentState === VgStates.STOP) {
						return;
					}

					if (isChange) {
						this.isBuffering = value;

						if (!isRemainsFalsy) {
							this.onUpdateBuffering();
						}
					}
				}
			}
		});

		//pass the API through our player ready callback
		this.onPlayerReady.emit({ vgAPI: this.vgApi, player: this });

		//state updating catch up, if necessary
		if (this.isStateUpdatedPriorToVgApi) {
			this.isStateUpdatedPriorToVgApi = false;
			this.onUpdateStateInternal(this.vgApi.currentState);
		}
	}

	public onSeekedInternal($currentTime: number): void {
		if (!this.isLive && !this.isComplete) {
			if(this.onSeek && !this.isRewind && !this.isBogusSeekEvent(this.seekStartTime, $currentTime)) {
				this.onSeek.emit({ startTime: this.seekStartTime, endTime: $currentTime });
			}

			if (!this.isRewind) {
				this.startHeartbeat(this.playerState);
			}
		}

		this.isSeeking = false;
		this.isRewind = false;
		this.playerState = this.playerState ? this.playerState : this.vgApi.currentState;
	}

	public onSeekingInternal($currentTime: number): void {
		if(!this.isSeeking && !this.isLive) {
			this.isSeeking = true;
			this.seekStartTime = $currentTime;
			this.stopHeartbeat();
		}
	}

	private onStopInternal(): void {
		this.playerState = VgStates.STOP;

		if (!this.isComplete && this.isLive && this.vgApi.activeSource) {
			this.vgApi.isBuffering = false;

			this.onStop.emit({ src: this.vgApi.activeSource.src });

			this.stopHeartbeat();
		}
	}

	private onUpdateBuffering(): void {
		//short-circuit if it's a live stream AND not playing, so that we don't emit events
		if (this.isLive && this.vgApi.currentState === VgStates.STOP) {
			return;
		}

		if (this.isBuffering) {
			this.onBufferingStartInternal();
		} else {
			this.onBufferingStopInternal();
		}
	}

	private onUpdateStartTime(): void {
		if (this.vgApi && this.startTime) {
			this.vgApi.onUpdateStartTime(this.startTime, true);
		}
	}

	public onResetStartTime(timeInSec: number): void {
		this.startTime = timeInSec;
	}

	public onUpdateStateInternal(state: string): void {
		if (!this.vgApi) {
			this.isStateUpdatedPriorToVgApi = true;
			return;
		}

		if (this.vgApi.analyticsEnabled && !this.isSeeking) {
			if (state !== this.playerState) {
				switch (state) {
					case VgStates.PLAY:
						this.onPlayInternal();
						break;

					case VgStates.PAUSE:
						this.onPauseInternal();
						break;

					case VgStates.STOP:
						this.onStopInternal();
						break;
				}
			} else if (state === VgStates.PLAY && !this.vgApi.isBuffering && !this.isSeeking && !this.isRewind) {
				this.startHeartbeat(VgStates.PLAY); // safety check
			}
		}
		else if(state === VgStates.PLAY) {
			//Hack to fix isComplete flag not resetting in video editor.
			this.isComplete = false;
		}
	}

	private startHeartbeat(currentState = this.vgApi.currentState): void {
		const heartbeatIntervalSec: number = +this.heartbeatInterval;

		if (heartbeatIntervalSec && !this.heartbeatIntervalRef && currentState === VgStates.PLAY) {
			this.heartbeatIntervalRef = window.setInterval(() => this.onHeartbeatInternal(), heartbeatIntervalSec * 1000); //sec to ms
		}
	}

	private stopHeartbeat(): void {
		if (this.heartbeatIntervalRef) {
			window.clearInterval(this.heartbeatIntervalRef);
			this.heartbeatIntervalRef = null;
		}
	}

	private manageVideoOverlays(): void {
		if (this.videoOverlays?.length && !this.videoOverlayManager) {
			this.videoOverlayManager = new VideoOverlayManager(this.vgApi.videogularElement, this.onOverlayAdded, this.onOverlayRemoved);
		}

		this.videoOverlayManager?.loadOverlays(this.videoOverlays);
	}

	public onResized($event: ResizedEvent): void {
		setTimeout(() => {
			const { height, width } = $event.newRect || {};
			this.PlayerSizeDetectionService.setPlayerSize(width, height);
			this.isSmallSizePlayer = this.PlayerSizeDetectionService.isSmallSizePlayer;
			this.el.nativeElement.style.setProperty('--player-videogular-height', height + 'px');
			this.el.nativeElement.style.setProperty('--player-videogular-width', width + 'px');
			this.onPlayerSizeChanged.emit({ height, width });
		}, 100);
	}

	public toggleReaction(e: any): void {
		this.reactionCfg.hidden = e;
	}

	public onFailedC2paTimeMarkers(markes: ITimeMarker[]): void {
		this.timeMarkers = [
			...(this.timeMarkers || []),
			...markes
		]
	}
}
